import React, { useState, useEffect } from 'react'
import { Doughnut, Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { 
  Globe, 
  Users, 
  Monitor, 
  Smartphone, 
  Tablet,
  ExternalLink,
  Loader,
  MapPin
} from 'lucide-react'
import { analyticsAPI, formatters } from '../services/analytics'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
)

function Traffic() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState('7daysAgo')
  const [activeTab, setActiveTab] = useState('sources')

  useEffect(() => {
    fetchTrafficData()
  }, [dateRange])

  const fetchTrafficData = async () => {
    try {
      setLoading(true)
      setError(null)
      const traffic = await analyticsAPI.getTraffic(dateRange)
      setData(traffic)
    } catch (err) {
      setError(err.message)
      console.error('Erro ao buscar dados de tráfego:', err)
    } finally {
      setLoading(false)
    }
  }

  const getDeviceIcon = (device) => {
    switch (device.toLowerCase()) {
      case 'desktop': return <Monitor className="w-5 h-5" />
      case 'mobile': return <Smartphone className="w-5 h-5" />
      case 'tablet': return <Tablet className="w-5 h-5" />
      default: return <Monitor className="w-5 h-5" />
    }
  }

  const getDeviceChartData = () => {
    if (!data?.devices) return null

    return {
      labels: data.devices.map(item => item.device),
      datasets: [
        {
          data: data.devices.map(item => item.sessions),
          backgroundColor: [
            '#3B82F6',
            '#10B981',
            '#F59E0B',
            '#EF4444',
            '#8B5CF6'
          ],
          borderWidth: 2,
          borderColor: '#ffffff'
        }
      ]
    }
  }

  const getChannelChartData = () => {
    if (!data?.sources) return null

    const channelData = data.sources.reduce((acc, source) => {
      const channel = source.channel
      if (!acc[channel]) {
        acc[channel] = 0
      }
      acc[channel] += source.sessions
      return acc
    }, {})

    return {
      labels: Object.keys(channelData),
      datasets: [
        {
          label: 'Sessões',
          data: Object.values(channelData),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1
        }
      ]
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  }

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatters.number(value)
          }
        }
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-lg text-gray-600">Carregando dados...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">Erro ao carregar dados</h3>
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchTrafficData}
          className="mt-4 btn-primary"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  const deviceChartData = getDeviceChartData()
  const channelChartData = getChannelChartData()

  const tabs = [
    { id: 'sources', name: 'Fontes de Tráfego', icon: Globe },
    { id: 'pages', name: 'Páginas Populares', icon: ExternalLink },
    { id: 'locations', name: 'Localização', icon: MapPin },
    { id: 'devices', name: 'Dispositivos', icon: Monitor }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Análise de Tráfego</h1>
          <p className="mt-2 text-gray-600">Análise detalhada das fontes e comportamento dos usuários</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="block w-full sm:w-auto px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="today">Hoje</option>
            <option value="yesterday">Ontem</option>
            <option value="7daysAgo">Últimos 7 dias</option>
            <option value="30daysAgo">Últimos 30 dias</option>
            <option value="90daysAgo">Últimos 90 dias</option>
          </select>
        </div>
      </div>

      {/* Gráficos de Resumo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Dispositivos</h3>
          <div className="h-64">
            {deviceChartData ? (
              <Doughnut options={chartOptions} data={deviceChartData} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">Dados não disponíveis</p>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Canais de Tráfego</h3>
          <div className="h-64">
            {channelChartData ? (
              <Bar options={barChartOptions} data={channelChartData} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">Dados não disponíveis</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navegação por Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-5 h-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Conteúdo das Tabs */}
      <div>
        {activeTab === 'sources' && (
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Fontes de Tráfego</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fonte / Medium
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Canal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sessões
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuários
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Taxa de Rejeição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duração Média
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.sources?.map((source, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {source.source}
                          </div>
                          <div className="text-sm text-gray-500">
                            {source.medium}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {source.channel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatters.number(source.sessions)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatters.number(source.users)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {source.bounceRate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {source.avgSessionDuration}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'pages' && (
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Páginas Mais Visitadas</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Página
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pageviews
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pageviews Únicos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tempo na Página
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Taxa de Rejeição
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.pages?.map((page, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {page.pageTitle}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {page.pagePath}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatters.number(page.pageviews)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatters.number(page.uniquePageviews)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {page.avgTimeOnPage}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {page.bounceRate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'locations' && (
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Localização dos Usuários</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Localização
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sessões
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuários
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.locations?.map((location, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {location.location}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatters.number(location.sessions)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatters.number(location.users)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'devices' && (
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Dispositivos dos Usuários</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data?.devices?.map((device, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      {getDeviceIcon(device.device)}
                      <h4 className="ml-2 text-lg font-semibold text-gray-900 capitalize">
                        {device.device}
                      </h4>
                    </div>
                    <span className="text-sm font-medium text-primary-600">
                      {device.percentage}%
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Sessões</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatters.number(device.sessions)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Usuários</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatters.number(device.users)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Taxa de Rejeição</span>
                      <span className="text-sm font-medium text-gray-900">
                        {device.bounceRate}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Última atualização */}
      <div className="text-center text-sm text-gray-500">
        Última atualização: {data?.lastUpdated ? 
          formatters.dateTime(data.lastUpdated) : 'N/A'
        }
      </div>
    </div>
  )
}

export default Traffic 